import { useEffect } from 'react';
import { useParams } from 'react-router';
import { Form, Input, Row } from 'antd';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import InputForm from 'components/InputForm/InputForm';
import SubmitButton from 'components/buttons/SubmitButton/SubmitButton';
import LinkButton from 'components/buttons/LinkButton/LinkButton';
import { getText } from 'utils/stringUtil';
import {
  WORK_EMAIL_ADDRESS,
  INVALID_EMAIL_FORMAT,
  PLEASE_ENTER_YOUR_WORK_EMAIL,
  NEW_PASSWORD,
  PASSWORD_REQUIREMENTS_MESSAGE,
  PLEASE_ENTER_YOUR_NEW_PASSWORD,
  CONFIRM_PASSWORD,
  PLEASE_ENTER_CONFIRMATION_PASSWORD,
  PASSWORDS_DO_NOT_MATCH,
  SAVE,
  CANCEL,
} from 'constants/languageConstants';
import {
  login,
  passwordReset,
  setEmployeeModal,
} from 'modules/auth/slices/employeeAuthSlice';

import styles from './resetPassword.module.less';

const ResetPassword = () => {
  const { hash = '', employerName = '' } = useParams();
  const baseUrl = `/${hash}/`;
  const { benguide } = useAppSelector((state) => state);
  const { data: benguideData } = benguide;
  const userEmail = useAppSelector((state) => state.employeeAuth.user);
  const token = useAppSelector((state) => state.employeeAuth.token);
  const inProgress = useAppSelector((state) => state.employeeAuth.inProgress);
  const language = useAppSelector((state) => state.benguide.data?.language);
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  const resetPasswordHandler = async (values: any) => {
    const constructRedirectURL = `benguideV2${baseUrl}${employerName}`;
    if (token) {
      const response = await dispatch(
        passwordReset(token, values?.confirmPassword?.trim())
      );
      if (response?.status === 201) {
        dispatch(
          login(
            values?.email?.trim(),
            values?.confirmPassword?.trim(),
            constructRedirectURL,
            benguideData?.planYearId
          )
        );
      }
    }
  };

  useEffect(() => {
    return () => {
      form?.resetFields();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    form.setFieldsValue({ email: userEmail });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  return (
    <Row justify="center" className={styles.wrapperContainer}>
      <InputForm form={form} layout="vertical" onFinish={resetPasswordHandler}>
        <Form.Item
          name="email"
          label={<>{getText(WORK_EMAIL_ADDRESS, language)}</>}
          rules={[
            {
              type: 'email',
              message: getText(INVALID_EMAIL_FORMAT, language),
            },
            {
              required: true,
              message: getText(PLEASE_ENTER_YOUR_WORK_EMAIL, language),
            },
          ]}
        >
          <Input
            disabled={!!userEmail}
            className={styles.inputWrapper}
            name="email"
            autoFocus
          />
        </Form.Item>
        <Form.Item
          name="newPassword"
          className={styles.formInput}
          label={<>{getText(NEW_PASSWORD, language)}</>}
          rules={[
            {
              pattern: new RegExp(
                /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,}$/
              ),
              message: getText(PASSWORD_REQUIREMENTS_MESSAGE, language),
            },
            {
              required: true,
              message: getText(PLEASE_ENTER_YOUR_NEW_PASSWORD, language),
            },
          ]}
        >
          <Input
            className={styles.inputWrapper}
            name="newPassword"
            type="password"
            autoFocus
            autoComplete="off"
          />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          className={styles.formInput}
          label={<>{getText(CONFIRM_PASSWORD, language)}</>}
          rules={[
            {
              required: true,
              message: getText(PLEASE_ENTER_CONFIRMATION_PASSWORD, language),
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(getText(PASSWORDS_DO_NOT_MATCH, language))
                );
              },
            }),
          ]}
        >
          <Input
            className={styles.inputWrapper}
            name="confirmPassword"
            type="password"
            autoComplete="off"
          />
        </Form.Item>
        <SubmitButton
          type="primary"
          htmlType="submit"
          disabled={inProgress}
          loading={inProgress}
          className={styles.submitPwdButton}
        >
          {getText(SAVE, language)}
        </SubmitButton>
        <Row justify="center">
          <LinkButton
            className={styles.resetPwdButton}
            onClick={() => dispatch(setEmployeeModal(false))}
          >
            {getText(CANCEL, language)}
          </LinkButton>
        </Row>
      </InputForm>
    </Row>
  );
};

export default ResetPassword;
